import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import type { AjustesTenant } from '@laser/core';
import { useAjustes, useGuardarAjustes, usePrecision } from '../api/hooks';
import type { CambiosAjustes } from '../api/tipos';
import { Campo, Cargando, Confirmar, EntradaNumero, EntradaPesos, ErrorCaja, mensajeError, useAvisar } from '../componentes/ui';
import { pesos, porcentaje, signoPct } from '../lib/formato';
import { enlaceWhatsApp, telefonoWhatsApp } from '../lib/whatsapp';

export const MINIMO_REVISADOS = 10;

/** Frase que acompaña al interruptor de pago automático. */
export function frasePrecision(revisados: number, dentro10: number): string {
  if (!revisados) return 'Todavía no hay trabajos revisados para comparar el estimado contra el precio final.';
  return `De los últimos ${revisados} estimados, ${dentro10} quedaron dentro de ±10% del precio final.`;
}

export default function Ajustes() {
  const q = useAjustes();
  const precision = usePrecision(20);
  const guardar = useGuardarAjustes();
  const avisar = useAvisar();
  const [a, setA] = useState<AjustesTenant | null>(null);
  const [confirmarAuto, setConfirmarAuto] = useState(false);
  useEffect(() => { if (q.data) setA(q.data); }, [q.data]);

  if (q.isLoading || !a) return q.isError ? <ErrorCaja error={q.error} reintentar={() => q.refetch()} /> : <Cargando />;
  const original = q.data!;
  const set = (c: Partial<AjustesTenant>) => setA((x) => x && { ...x, ...c });
  const cambios: CambiosAjustes = Object.fromEntries(
    (Object.keys(a) as (keyof AjustesTenant)[]).filter((k) => k !== 'tenantId' && k !== 'pagoAutomatico' && a[k] !== original[k]).map((k) => [k, a[k]]),
  );
  const hayCambios = Object.keys(cambios).length > 0;
  const valido = a.margen >= 1 && a.urgentePct >= 0 && a.diasEstandar >= 0 && a.diasUrgente >= 0 && a.factorTraslado >= 1 && /^\d{10,15}$/.test(a.whatsapp) && /.+@.+\..+/.test(a.correoAvisos);

  const p = precision.data;
  const pocos = !p || p.revisados < MINIMO_REVISADOS;
  const fraccion = p && p.revisados ? p.dentro10 / p.revisados : 0;

  const cambiarAuto = (valor: boolean) => guardar.mutate({ pagoAutomatico: valor }, {
    onSuccess: () => { setConfirmarAuto(false); avisar(valor ? 'Pago automático activado' : 'Pago automático desactivado', 'ok'); },
    onError: (e) => avisar(mensajeError(e), 'bad'),
  });

  return (
    <>
      <div className="titulo"><h1>Ajustes</h1><span className="sub">Precios y avisos del taller</span></div>

      <div className="rejilla-detalle">
        <section className="tarjeta" aria-labelledby="precio-t">
          <h2 id="precio-t">Precio</h2>
          <div className="campos">
            <Campo etiqueta="Preparación por trabajo" htmlFor="aj-prep"><EntradaPesos id="aj-prep" valor={a.preparacionCentavos} onCambio={(c) => set({ preparacionCentavos: c ?? 0 })} /></Campo>
            <Campo etiqueta="Mínimo por trabajo" htmlFor="aj-min"><EntradaPesos id="aj-min" valor={a.minimoCentavos} onCambio={(c) => set({ minimoCentavos: c ?? 0 })} /></Campo>
            <Campo etiqueta="Margen" htmlFor="aj-margen" ayuda={`Multiplica el costo: ${signoPct(a.margen - 1)}`}>
              <EntradaNumero id="aj-margen" valor={a.margen} paso={0.05} min={1} sufijo="×" onCambio={(n) => set({ margen: n })} />
            </Campo>
            <Campo etiqueta="Recargo urgente" htmlFor="aj-urg">
              <EntradaNumero id="aj-urg" valor={Math.round(a.urgentePct * 100)} min={0} sufijo="%" onCambio={(n) => set({ urgentePct: n / 100 })} />
            </Campo>
            <Campo etiqueta="Días hábiles estándar" htmlFor="aj-de"><EntradaNumero id="aj-de" valor={a.diasEstandar} min={0} sufijo="días" onCambio={(n) => set({ diasEstandar: Math.round(n) })} /></Campo>
            <Campo etiqueta="Días hábiles urgente" htmlFor="aj-du"><EntradaNumero id="aj-du" valor={a.diasUrgente} min={0} sufijo="días" onCambio={(n) => set({ diasUrgente: Math.round(n) })} /></Campo>
            <Campo etiqueta="Factor de traslado" htmlFor="aj-ft" ayuda={`Movimientos en vacío: ${signoPct(a.factorTraslado - 1)} sobre el corte`}>
              <EntradaNumero id="aj-ft" valor={a.factorTraslado} paso={0.01} min={1} sufijo="×" onCambio={(n) => set({ factorTraslado: n })} />
            </Campo>
          </div>

          <h2 style={{ marginTop: 18 }}>Avisos</h2>
          <div className="campos">
            <Campo etiqueta="WhatsApp del taller" htmlFor="aj-wa" ayuda={<>Con lada de país, sólo dígitos. <a href={enlaceWhatsApp(a.whatsapp, 'Prueba desde el panel de láser')} target="_blank" rel="noopener noreferrer">Probar</a></>}>
              <input id="aj-wa" inputMode="tel" value={a.whatsapp} onChange={(e) => set({ whatsapp: e.target.value.replace(/\D/g, '') })}
                onBlur={() => set({ whatsapp: telefonoWhatsApp(a.whatsapp) })} />
            </Campo>
            <Campo etiqueta="Correo de avisos" htmlFor="aj-correo" ayuda="Aquí llega el aviso de cada trabajo nuevo.">
              <input id="aj-correo" type="email" value={a.correoAvisos} onChange={(e) => set({ correoAvisos: e.target.value.trim() })} />
            </Campo>
          </div>

          <div className="fila" style={{ justifyContent: 'flex-end', marginTop: 16 }}>
            {!valido && <span className="pendiente">Revisa los campos (margen ≥ 1, WhatsApp 10–15 dígitos, correo válido).</span>}
            <button type="button" className="btn" disabled={!hayCambios || guardar.isPending} onClick={() => setA(original)}>Deshacer</button>
            <button type="button" className="btn btn--marca" disabled={!hayCambios || !valido || guardar.isPending}
              onClick={() => guardar.mutate(cambios, { onSuccess: () => avisar('Ajustes guardados', 'ok'), onError: (e) => avisar(mensajeError(e), 'bad') })}>
              {guardar.isPending ? 'Guardando…' : 'Guardar ajustes'}
            </button>
          </div>
        </section>

        <section className="tarjeta pila" aria-labelledby="auto-t">
          <h2 id="auto-t" style={{ marginBottom: 0 }}>Pago automático</h2>
          <p className="muted" style={{ fontSize: '.9rem' }}>
            Apagado: el cliente ve un precio estimado y tú revisas antes de mandar el link de pago.
            Encendido: el link se crea al instante con el precio calculado.
          </p>
          <label className="interruptor">
            <input type="checkbox" role="switch" checked={original.pagoAutomatico} disabled={guardar.isPending}
              onChange={(e) => (e.target.checked ? setConfirmarAuto(true) : cambiarAuto(false))} />
            {original.pagoAutomatico ? 'Encendido' : 'Apagado'}
          </label>

          <div className="h3" style={{ marginTop: 6 }}>Qué tan bien estima el cotizador</div>
          {precision.isLoading ? <Cargando /> : precision.isError ? <ErrorCaja error={precision.error} reintentar={() => precision.refetch()} /> : p && (
            <>
              <p style={{ fontSize: '.95rem' }}><b>{frasePrecision(p.revisados, p.dentro10)}</b></p>
              {p.revisados > 0 && (
                <>
                  <div className="barra-precision" role="img" aria-label={`${porcentaje(fraccion)} dentro de ±10%`}>
                    <i style={{ width: `${fraccion * 100}%`, background: 'var(--good)' }} />
                    <i style={{ width: `${(1 - fraccion) * 100}%`, background: 'var(--warn)', opacity: 0.55 }} />
                  </div>
                  <div className="fila fila--entre meta"><span>✓ {p.dentro10} dentro de ±10%</span><span>{p.revisados - p.dentro10} fuera · error medio {porcentaje(p.errorMedio)}</span></div>
                </>
              )}
              {pocos && (
                <div className="aviso aviso--warn">
                  Hay {p.revisados} {p.revisados === 1 ? 'trabajo revisado' : 'trabajos revisados'}; con menos de {MINIMO_REVISADOS} todavía no es buena idea encender el pago automático.
                </div>
              )}
              {p.muestras.length > 0 && (
                <details className="secc">
                  <summary><span className="h3">Ver trabajos</span><span className="res">{p.muestras.length}</span></summary>
                  <div className="cuerpo">
                    <table className="comparar">
                      <thead><tr><th>Folio</th><th className="num">Estimado</th><th className="num">Final</th><th className="num">Dif.</th></tr></thead>
                      <tbody>
                        {p.muestras.map((m) => (
                          <tr key={m.trabajoId}>
                            <td><Link className="folio" to={`/trabajos/${m.trabajoId}`}>{m.folio}</Link></td>
                            <td className="num">{pesos(m.estimadoCentavos)}</td>
                            <td className="num">{pesos(m.finalCentavos)}</td>
                            <td className={`num ${Math.abs(m.diferencia) > 0.1 ? 'cambio-mas' : ''}`}>{signoPct(m.diferencia)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </details>
              )}
            </>
          )}
        </section>
      </div>

      <Confirmar abierto={confirmarAuto} titulo="¿Encender el pago automático?" tono={pocos ? 'peligro' : 'marca'} textoConfirmar="Encender"
        ocupado={guardar.isPending} onCancelar={() => setConfirmarAuto(false)} onConfirmar={() => cambiarAuto(true)}>
        <p style={{ marginTop: 6 }}>Los clientes van a pagar el precio calculado sin que lo revises.</p>
        {p && <p style={{ marginTop: 8 }}>{frasePrecision(p.revisados, p.dentro10)}</p>}
        {pocos && <p className="aviso aviso--warn" style={{ marginTop: 8 }}>Menos de {MINIMO_REVISADOS} trabajos revisados: la estimación todavía no está probada.</p>}
      </Confirmar>
    </>
  );
}
